import { makeStyles } from '@material-ui/styles'
import * as IpAddress from 'ip-address'
import React from 'react'
import { IPVersions } from 'app/constants'
import { listAllVirtualMachines } from 'app/plugins/openstack/components/vms/actions'
import { allVirtualMachinesSelector } from 'app/plugins/openstack/components/vms/selectors'
import { useAppSelector } from 'app/store'
import Progress from 'core/components/progress/Progress'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import DropdownField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import useListAction from 'core/hooks/useListAction'
import { customValidator, FieldValidator } from 'core/utils/fieldValidators'
import {
  isValidIpv4Address,
  isValidIpv4Cidr,
  isValidIpv6Address,
  isValidIpv6Cidr,
} from 'core/utils/validators'
import { memoize } from 'utils/misc'
import AllocationPoolsField from './AllocationPoolsField'
import DnsNameServersField from './DnsNameServersField'
import HostRoutesField from './HostRoutesField'
import IPv6AddressConfigurationModePicklist from './IPv6AddressConfigurationModePicklist'
import IpVersionPicklist from './IpVersionPicklist'

export const cidrValidator = customValidator((value, formValues) => {
  if (!value) {
    return false
  }
  return formValues.ipVersion === IPVersions.IPv4 ? isValidIpv4Cidr(value) : isValidIpv6Cidr(value)
}, 'Invalid CIDR provided')

export const gatewayInCidrValidator = customValidator((value, formValues) => {
  if (!value) {
    return true
  }
  try {
    const testCidr =
      formValues.ipVersion === IPVersions.IPv4
        ? new IpAddress.Address4(formValues.cidr)
        : new IpAddress.Address6(formValues.cidr)
    return formValues.ipVersion === IPVersions.IPv4
      ? new IpAddress.Address4(value).isInSubnet(testCidr)
      : new IpAddress.Address6(value).isInSubnet(testCidr)
  } catch (err) {
    return false
  }
}, 'Gateway IP address is not inside of designated CIDR')

export const gatewayIpValid = customValidator((value, formValues) => {
  if (!value) {
    return true
  }
  return formValues.ipVersion === IPVersions.IPv4
    ? isValidIpv4Address(value)
    : isValidIpv6Address(value)
}, 'Gateway IP address is an invalid IP address')

export const gatewayIpInUseValidator = memoize(
  (usedIps) =>
    new FieldValidator((value) => !usedIps.includes(value), `Gateway IP address is already in use`),
)

export function BasicSubnetFields({
  inNetworkCreation = false,
  inSubnetEdit = false,
  params,
  getParamsUpdater,
}) {
  const classes = useStyles()

  // TODO: PERFORMANCE IMPACT - This will load all VMs every time the subnet form is opened
  // const { loading: loadingAllVms } = useListAction(listAllVirtualMachines)
  // const allVms = useAppSelector(allVirtualMachinesSelector)

  // const usedIpsByVms = React.useMemo(() => {
  //   const allNetworks = allVms?.map((vm) => vm?.addresses)

  //   const ips = allNetworks?.flatMap((serverNetworks) => {
  //     const allInterfaces = Object.values(serverNetworks)

  //     return allInterfaces?.flatMap((networkInterfaces) =>
  //       networkInterfaces?.map((interfaceInfo) => interfaceInfo?.addr),
  //     )
  //   })

  //   return ips || []
  // }, [allVms])

  return (
    <FormFieldSection title="Subnet Configuration">
      <TextField
        id={inNetworkCreation ? 'subnetName' : 'name'}
        label="Name"
        onChange={getParamsUpdater(inNetworkCreation ? 'subnetName' : 'name')}
        value={inNetworkCreation ? params.subnetName : params.name}
        required
      />
      <DropdownField
        DropdownComponent={IpVersionPicklist}
        id="ipVersion"
        label="IP Version"
        onChange={getParamsUpdater('ipVersion')}
        value={params.ipVersion}
        required
        disabled={inSubnetEdit}
      />
      <TextField
        id="cidr"
        label="Network Address (CIDR)"
        onChange={getParamsUpdater('cidr')}
        value={params.cidr}
        info={`Specify your network CIDR used to designate your IP address pool (e.g., ${
          params.ipVersion === IPVersions.IPv6 ? '2001:db8:0:7:af:e2:c1:5/128' : '192.0.2.0/24'
        }).`}
        required
        validations={[cidrValidator]}
        disabled={inSubnetEdit}
      />
      <div className={classes.gatewayIp}>
        <TextField
          id="gatewayIp"
          label="Gateway IP"
          onChange={getParamsUpdater('gatewayIp')}
          info="Specify the IP address used for the network gateway. If left blank, the first available IP address within your network CIDR will be automatically chosen."
          value={params.gatewayIp}
          validations={[
            gatewayIpValid,
            gatewayInCidrValidator,
            // gatewayIpInUseValidator(usedIpsByVms),
          ]}
        />
      </div>
      <CheckboxField
        id="disableGateway"
        label="Disable Gateway"
        value={params.disableGateway}
        onChange={getParamsUpdater('disableGateway')}
        info="By checking this option, no network gateway will be created. You may choose to add one later in the subnet edit form."
      />
      <CheckboxField
        id="enableDhcp"
        label="Enable DHCP"
        value={params.enableDhcp}
        onChange={getParamsUpdater('enableDhcp')}
        info="Check to enable DHCP on this network."
        tooltipAlign={{
          vertical: 'middle',
          horizontal: 'left',
        }}
      />
      <CheckboxField
        id="dns_publish_fixed_ip"
        label="DNS Publish Fixed IP"
        value={params.dns_publish_fixed_ip}
        onChange={getParamsUpdater('dns_publish_fixed_ip')}
        info="Check to enable DNS records creation for any ports or VMs created on this subnet."
        disabled={!params.dns_domain}
        disabledTooltip="DNS Zone is required to enable this option."
        tooltipAlign={{
          vertical: 'middle',
          horizontal: 'left',
        }}
      />
      {/* IPv6AddressConfigurationMode fields are non editable */}
      {!inSubnetEdit && (
        <IPv6AddressConfigurationModePicklist params={params} getParamsUpdater={getParamsUpdater} />
      )}
    </FormFieldSection>
  )
}

export function AdvancedSubnetFields({ params, updateParams }) {
  return (
    <FormFieldSection title="Subnet Details">
      <AllocationPoolsField params={params} updateParams={updateParams} />
      <DnsNameServersField params={params} updateParams={updateParams} />
      <HostRoutesField params={params} updateParams={updateParams} />
    </FormFieldSection>
  )
}

const useStyles = makeStyles((theme) => ({
  gatewayIp: {
    width: '400px',
  },
}))
